import { INumberIncrementProductItem } from '@core/form/number-increment/number-increment-product-item.model';
import { IProduct } from '@core/product/models/product.model';
import { IProductSession, TWhenAttendingProductSession } from '@core/product/models/product-session.model';
import { IProductInfo } from '@core/product/models/product-info.model';
import { IProductGiftcard, IUniqueGiftCard } from '@core/product/models/product-giftcard.model';
import { hexGenerate } from '@roller/rdl/utilities/string';
import { Injectable } from '@angular/core';
import { DateFormatService } from '@roller/rdl/utilities/date';
import { CMembership, CMembershipRenewal } from '@app/tickets/tickets.model';
import { IWhenAttending } from '@app/when-attending/when-attending.model';
import { ProductHelperService } from '@core/product/product-helper.service';
import { LoggerService } from '@core/logger/logger.service';
import { IOrderItem } from '@core/order/models/order-item.model';
import { IOrder } from '@core/order/models/order.model';
import { TProductType } from '@core/product/models/product-type.type';
import { WhenAttendingSessionService } from '@app/when-attending/when-attending-session/when-attending-session.service';
import { IProductPackageItem } from '@core/product/models/product-package-item.model';
import { TranslateService } from '@ngx-translate/core';
import { IOrderItemCreateOptions } from '@core/order/models/order.service.model';
import { OrderTicketRatiosService } from './order-ticket-ratios.service';
import { ProductMembershipsService } from '@app/products/product-memberships/product-memberships.service';
import { ProductService } from '@core/product/product.service';
import { IOrderItemSelection } from '../checkout/model/checkout-order-item-selection.model';
import { IOptionSetSelection } from '../checkout/model/checkout-option-set-selection.model';
import { VenueConfigService } from '@core/venue-config/venue-config.service';
import { OrderPartyPackageService } from './order-party-package.service';
import { CMaxOrderItems } from '@core/availability-calculation/availability-calculation.const';

@Injectable({
  providedIn: 'root'
})
export class OrderItemService {
  constructor(
    private dateService: DateFormatService,
    private productHelperService: ProductHelperService,
    private loggerService: LoggerService,
    private whenAttendingSessionService: WhenAttendingSessionService,
    private dateFormatService: DateFormatService,
    private translateService: TranslateService,
    private orderTicketRatiosService: OrderTicketRatiosService,
    private productMembershipsService: ProductMembershipsService,
    private productService: ProductService,
    private venueConfigService: VenueConfigService,
    private orderPartyPackageService: OrderPartyPackageService
  ) {}

  /** Total cost should always be set, but since it's calculated after we create the item it's marked as undefined */
  getItemTotalCost(item: IOrderItem): number {
    return item.totalCost || item.cost * item.quantity;
  }

  getTotalPostageCost(items: IOrderItem[]): number {
    let totalPostageCost = 0;

    items.forEach((item) => {
      item.uniqueGiftCards?.forEach((giftCard) => {
        totalPostageCost += giftCard.postage?.price || 0;
      });
    });

    return totalPostageCost;
  }

  /** Returns an array of order items to be used in cost calculations */
  getItemsForCalculations(orderItems: IOrderItem[], isEditBooking: boolean): IOrderItem[] {
    let items = orderItems.filter((item) => item.productType !== 'package' && item.productType !== 'partypackage');
    const partyPackageOrderItems = orderItems.filter((item) => item.productType === 'partypackage');
    const packageOrderItems = orderItems.filter((item) => item.productType === 'package');
    items = [
      ...items,
      ...this.orderPartyPackageService.getPartyPackageItemsForCalcs(partyPackageOrderItems),
      ...this.getPackageItemsForCalcs(packageOrderItems)
    ];
    items = items.filter((item: IOrderItem) => !this.productMembershipsService.isUnmodifiedMembership(item));

    return items.map((item) => {
      return {
        ...item,
        giftCardPostageItems: [{ price: this.getTotalPostageCost([item]) }],
        perProductBookingFee: this.getPerProductFeeForCalcs(item, isEditBooking)
      };
    });
  }

  /** Returns an array of required gift card forms based on the quantity of order items */
  getUniqueGiftCards(quantity: number, type: TProductType | undefined): IUniqueGiftCard[] | undefined {
    if (type !== 'giftcard') {
      return;
    }

    return new Array(quantity).fill(0).map(() => {
      return { formId: hexGenerate(6) };
    });
  }

  /**
   * Splits related basket items into items that can be kept (numbers reduced) and those that need to be removed - at this point, the remove items quantity is already updated
   * @param removeItem The item to remove from the basket
   * @param relatedBasketItems Filtered list of the checkoutBasket with only items with same relatedOrderItemKey
   * @param includeMinPurchaseItems when true, this will auto-remove invalid min purchase tickets (siblings) when a sibling is removed
   * @returns [removedItems] - the list of Items to be removed.
   */
  getAffectedRemovableItems(
    removeItem: IOrderItem,
    relatedBasketItems: IOrderItem[],
    includeMinPurchaseItems = true
  ): IOrderItem[] {
    let totalQuantity: number = removeItem.quantity ? removeItem.quantity : removeItem.oldQuantity || 1;
    const isCompulsoryItemRemoved = removeItem.product.forceMinPurchase || false;
    const uniqueIdsToRemove = [removeItem.uniqueId];
    let isMinPurchaseInvalid = false;

    // check for presence of compulsory tickets
    if (relatedBasketItems.some((info) => info.product.forceMinPurchase)) {
      let totalCompulsoryAffected: IOrderItem[] = [removeItem];
      // if the item to be removed is the compulsory item
      if (removeItem.product.forceMinPurchase) {
        totalCompulsoryAffected = relatedBasketItems.filter((basket: IOrderItem) => {
          if (basket.isForcedPurchase || basket.parentProductId !== removeItem.parentProductId) {
            return false;
          }

          if (!uniqueIdsToRemove.includes(basket.uniqueId)) {
            uniqueIdsToRemove.push(basket.uniqueId);
          }

          return true;
        });
      }

      totalQuantity += totalCompulsoryAffected?.reduce(
        (acc: number, basket: IOrderItem) => (acc += basket.quantity),
        0
      );
    }

    if (includeMinPurchaseItems && removeItem.parentMinPurchase && removeItem.parentMinPurchase > 1) {
      const siblingVariations = relatedBasketItems.filter(
        (item) => item.parentProductId === removeItem.parentProductId && item.uniqueId !== removeItem.uniqueId
      );
      const siblingTotalQuantity = siblingVariations.reduce((quantity, basket) => (quantity += basket.quantity), 0);

      isMinPurchaseInvalid = siblingTotalQuantity + removeItem.quantity < removeItem.parentMinPurchase;
    }

    const filteredBasket: IOrderItem[] = [];
    const removedItems: IOrderItem[] = [];
    const isRemoveItemParent = !removeItem.addonToProductId;
    const removeFromCart = (item: IOrderItem): void => {
      item.oldQuantity = item.quantity;
      item.quantity = 0;
      removedItems.push(item);
    };

    relatedBasketItems.forEach((item: IOrderItem) => {
      if (uniqueIdsToRemove.includes(item.uniqueId)) {
        removeFromCart(item);
      } else if (isCompulsoryItemRemoved && (item.isCompulsoryPeer || item.isCompulsoryPurchase)) {
        removeFromCart(item);
      } else if (isMinPurchaseInvalid) {
        removeFromCart(item);
      } else if (item.isForcedPurchase && isRemoveItemParent) {
        if (this.canRemoveExactQuantity(item.product, item, totalQuantity)) {
          const quantity = item.quantity - totalQuantity;
          item.oldQuantity = item.quantity;
          item.quantity = quantity >= 0 ? quantity : 0;
          removedItems.push(item);
          if (quantity > 0) {
            filteredBasket.push(item);
          }
        } else {
          removeFromCart(item);
        }
      } else {
        filteredBasket.push(item); // a regular addon was removed, do nothing
      }
    });

    // If only addons are left to return, then that means their parent item has been removed and currently they are also to be removed
    if (filteredBasket.every((basketItem: IOrderItem) => basketItem.addonToProductId)) {
      filteredBasket.forEach((basketItem: IOrderItem) => removeFromCart(basketItem));
    }

    return removedItems;
  }

  orderItemsAllowDeposit(basket: IOrderItem[]): boolean {
    return basket.some(
      (orderItem: IOrderItem) =>
        !!orderItem.depositAmount || (orderItem.costPercentageRequired > 0 && orderItem.costPercentageRequired < 100)
    );
  }

  /**
   * Validates if a specific item can be removed from the order without breaking min/forced purchase rules
   */
  canRemoveExactQuantity(product: IProductInfo, orderItem: IOrderItem, quantityToRemove = 1) {
    if (!product || !orderItem) {
      return false;
    }

    return (
      orderItem.quantity > 0 &&
      (!product.minPurchase ||
        !product.forceMinPurchase ||
        (orderItem.quantity - quantityToRemove) * product.groupSize >= product.minPurchase)
    );
  }

  /** Sorts a list of order items and finds  the one with the earliest booking date */
  getEarliestBookingItem(order: IOrderItem[] | undefined): IOrderItem | undefined {
    if (!order || !order.length) {
      return undefined;
    }

    return order
      .filter((item: IOrderItem) => !!item.bookingDate)
      .sort((a: IOrderItem, b: IOrderItem) => a.bookingDate.getTime() - b.bookingDate.getTime())[0];
  }

  /**
   * Called after product form quantity change or during current max calculations find if product in question exists in the basket
   * 1) Checks for matching date if the current product is date based
   * 2) Check matching session time if the current product is a session
   * 3) Check matching product id
   *
   * @param items order items to check against
   * @param parentProduct
   * @param selectedDate
   * @param selectedSession
   *
   * @param product from the number increment form or available tickets
   * @param matchingId override to match an id instead of an entire product object || unique orderItem id when custom value matching
   * @param isCustomValue match by unique Id
   */
  getMatchingOrderItem(
    items: IOrderItem[],
    parentProduct: IProduct,
    selectedDate: IWhenAttending | undefined,
    selectedSession: IProductSession | undefined,
    product?: INumberIncrementProductItem | IProductInfo,
    matchingId?: string,
    isCustomValue = false
  ): IOrderItem | undefined {
    /** Custom value products are matched by unique id as there could be multiple with the same productId and value */
    if (isCustomValue) {
      return items.find((item: IOrderItem) => matchingId && item.uniqueId === matchingId);
    }

    return items.find((item: IOrderItem) => {
      // don't check basket items (tickets) that are not on the same day
      if (
        (parentProduct.isItemDateBased || !!item.addonToProductId) &&
        selectedDate &&
        !this.dateService.isSameDay(item.bookingDate, selectedDate?.date)
      ) {
        return false;
      }

      // rule out if the parentProduct is sessionBased does not match the current selected session
      // Note that the product does not have to be session based
      if (
        (parentProduct.isItemSessionBased || !!item.addonToProductId) &&
        (selectedSession || item.session) &&
        !this.productHelperService.isSameSession(item.session, selectedSession)
      ) {
        return false;
      }

      return product ? item.productId === product.id : item.productId === matchingId;
    });
  }

  /** @returns a list of products tickets that are currently in the list of order items */
  getSelectedOrderTickets(
    orderItems: IOrderItem[],
    product: IProduct,
    tickets: IProductInfo[],
    session: TWhenAttendingProductSession | undefined,
    selectedDate: IWhenAttending | undefined
  ): IOrderItem[] {
    return tickets.reduce((filtered: IOrderItem[], productInfo: IProductInfo) => {
      const selectedSession = this.whenAttendingSessionService.getSelectedSession(session, productInfo);

      const ticket = this.getMatchingOrderItem(orderItems, product, selectedDate, selectedSession, productInfo);

      if (ticket) {
        filtered.push(ticket);
      }

      return filtered;
    }, []);
  }

  getMatchingOrderItemByItem(order: IOrder, orderItem: IOrderItem): IOrderItem | undefined {
    return order.items.find((item) => this.isOrderItemMatch(orderItem, item));
  }

  isOrderItemMatch(basketItem: IOrderItem, processItem: IOrderItem) {
    if (basketItem.uniqueId === processItem.uniqueId) {
      return true;
    }

    if (
      basketItem.relatedOrderItemKey &&
      processItem.relatedOrderItemKey &&
      basketItem.relatedOrderItemKey !== processItem.relatedOrderItemKey
    ) {
      return false;
    }

    if (basketItem.product.isItemSessionBased || (basketItem.productType === 'addon' && processItem.session)) {
      return (
        processItem.productId === basketItem.productId &&
        this.productHelperService.isSameSession(processItem.session, basketItem.session)
      );
    }

    if (basketItem.product.isItemDateBased) {
      return (
        processItem.productId === basketItem.productId &&
        this.dateService.isSameDay(processItem.bookingDate, basketItem.bookingDate)
      );
    }

    if (basketItem.productType === 'giftcard' || basketItem.productType === 'wallet') {
      return processItem.productId === basketItem.productId && processItem.cost === basketItem.cost;
    }

    if (basketItem.manageBookingType === CMembership || basketItem.manageBookingType === CMembershipRenewal) {
      return false;
    }

    return processItem.productId === basketItem.productId;
  }

  buildRescheduledItems(orderItems: IOrderItem[], bookingDate: Date, session?: IProductSession): IOrderItem[] {
    return orderItems.map((orderItem) => ({
      ...orderItem,
      isRescheduling: true,
      bookingDate: bookingDate,
      session: session
    }));
  }

  createOrderItem(param: IOrderItemCreateOptions): IOrderItem {
    const change = param.change;
    if (param.isAddon) {
      const parentOrderItem = param.relatedOrderItems.find(
        (item: IOrderItem) => item.uniqueId === param.parentUniqueId
      );
      if (!parentOrderItem) {
        this.loggerService.error('Addon error: Unable to find parent basket item', {
          message: `parentUniqueId: ${param.parentUniqueId}; addon item parentId: ${
            param.change.parentProduct.id
          }; basket: ${JSON.stringify(param.relatedOrderItems)}`
        });
      }

      return this.buildAddonOrderItem(
        change.ev,
        change.parentProduct,
        param.bookingDate,
        change.selectedSession || change.ev.session,
        parentOrderItem,
        false,
        param.relatedOrderItemKey
      );
    } else {
      return this.buildOrderItem(
        change.ev,
        change.parentProduct,
        param.bookingDate,
        change.selectedSession || change.ev.session,
        param.addonList || [],
        param.relatedOrderItemKey
      );
    }
  }

  /**
   * Takes the information from the NumberIncrement Item and converts to an OrderItem.
   * Used when a product gets added as an addon to another product.
   */
  buildAddonOrderItem(
    ev: INumberIncrementProductItem,
    parentProduct: IProduct,
    bookingDate: Date,
    session: IProductSession | undefined,
    associatedProduct: IOrderItem | undefined,
    isForcedPurchase: boolean,
    relatedOrderItemKey?: string
  ): IOrderItem {
    return this.buildGenericOrderItem(
      ev,
      parentProduct,
      bookingDate,
      session,
      relatedOrderItemKey,
      associatedProduct?.parentProductId,
      associatedProduct?.hideSessionDuration,
      [],
      isForcedPurchase
    );
  }

  /**
   * Takes the information from the NumberIncrement Item and converts to an OrderItem.
   * When passed an addonList, it will assign this to the OrderItem - retains updated addon info for the specific OrderItem
   */
  buildOrderItem(
    ev: INumberIncrementProductItem,
    parentProduct: IProduct,
    bookingDate: Date,
    session: IProductSession | undefined,
    addonList: IProduct[],
    relatedOrderItemKey?: string
  ): IOrderItem {
    return this.buildGenericOrderItem(
      ev,
      parentProduct,
      bookingDate,
      session,
      relatedOrderItemKey,
      undefined,
      undefined,
      addonList,
      false
    );
  }

  private buildGenericOrderItem(
    ev: INumberIncrementProductItem,
    parentProduct: IProduct,
    bookingDate: Date,
    session: IProductSession | undefined,
    relatedOrderItemKey: string | undefined,
    addonToProductId: string | undefined,
    addonHideSessionDuration: boolean | undefined,
    addonList: IProduct[] | undefined,
    isForcedPurchase: boolean | undefined
  ): IOrderItem {
    const product: IProductInfo = {
      ...ev.product,
      validDays: parentProduct.validDays,
      validDaysFromRedemption: parentProduct.validDaysFromRedemption,
      termsAndConditionsText: parentProduct.termsAndConditionsText
    };

    const item: IOrderItem = {
      uniqueId: hexGenerate(15),
      productId: ev.id,
      parentProductId: parentProduct.id,
      productType: parentProduct.type as TProductType,
      product: structuredClone(product),
      parentName: parentProduct.name,
      parentMinPurchase: parentProduct.minPurchase,
      quantity: ev.quantity,
      bookingDate: this.getOrderItemBookingDate(product, bookingDate),
      session,
      isPerGuestItem: parentProduct.durationType === 'perGuest',
      isLinkedToConqueror: this.productHelperService.isProductLinkedToConqueror(parentProduct),
      groupSize: ev.product.groupSize,
      cost: ev.product.cost,
      tax: ev.product.tax,
      isTaxInclusive: ev.product.isTaxInclusive,
      addonList: parentProduct.addonList || addonList || [],
      depositAmount: parentProduct.depositAmount,
      costPercentageRequired: parentProduct.depositPercentage || 0,
      perProductBookingFee: ev.product.perProductBookingFee || ev.product.fee,
      recurringPayment: ev.product.recurringPayment,
      availabilities: parentProduct.availabilities,
      formIds: parentProduct.formIds,
      addonToProductId,
      relatedOrderItemKey: relatedOrderItemKey || hexGenerate(15),
      discount: 0,
      giftcard: this.getOrderItemGiftCard(parentProduct, product),
      uniqueGiftCards: this.getUniqueGiftCards(ev.quantity, parentProduct.type),
      captureTicketHolderName: parentProduct.captureTicketHolderName,
      isForcedPurchase,
      isForcedPurchaseRemovable: this.productHelperService.isForcedPurchaseRemovable(parentProduct),
      hideSessionDuration:
        addonHideSessionDuration !== undefined ? addonHideSessionDuration : parentProduct.hideSessionDuration,
      packageItems: product.packageItems,
      sessionDiscount: product.appliedSessionDiscount,
      pricingRule: product.pricingRule,
      orderSelections: [],
      optionSetSelections: [],
      parentMaxPurchase: parentProduct.maxPurchase,
      additionalGuests: ev.additionalGuests,
      ticketQuantity: ev.quantity * ev.product.groupSize + ev.additionalGuests,
      isUserDefinedGroupSize: this.productHelperService.isUserDefinedGroupSize(ev.product),
      lastUpdatedDate: new Date()
    };

    item.packageOrderItems = this.getPackageOrderItems(item, bookingDate);

    if (ev.ticketQuantity) {
      return this.setItemQuantityByGuests(item, ev.ticketQuantity);
    }

    return item;
  }

  mapToOrderSelections(
    parentProductId: number | string,
    productId: number | string,
    quantity: number,
    parentName: string | undefined,
    productName: string | undefined
  ): IOrderItemSelection {
    return {
      parentProductId: parentProductId.toString(),
      productId: productId.toString(),
      quantity: quantity,
      parentProductName: parentName || '',
      productName: productName || ''
    };
  }

  mapToOptionSetSelections(
    productOptionSetId: number | string,
    productId: number | string,
    quantity: number,
    productOptionSetName: string | undefined,
    productName: string | undefined,
    displayOrder: number | undefined
  ): IOptionSetSelection {
    return {
      productPackageOptionSetId: productOptionSetId.toString(),
      productId: productId.toString(),
      quantity: quantity,
      productPackageOptionSetName: productOptionSetName || '',
      productName: productName || '',
      displayOrder: displayOrder || CMaxOrderItems
    };
  }

  /** Sets 'invalid item' messages on each order item - should be run when the order is update */
  getItemsWithInvalidMessage(orderItems: IOrderItem[]) {
    return orderItems.map((item: IOrderItem) => {
      return {
        ...item,
        invalidItemMessage: this.getItemInvalidMessage(item, orderItems)
      };
    });
  }

  setItemQuantityByGuests(item: IOrderItem, ticketQuantity: number): IOrderItem {
    if (!this.productHelperService.isUserDefinedGroupSize(item.product)) {
      return item;
    }

    const guestPerQuantity = this.productHelperService.getGuestPerQuantity(item.product);
    const quantity = Math.ceil(ticketQuantity / guestPerQuantity);
    const additionalGuests = ticketQuantity - quantity * item.groupSize;

    return { ...item, quantity, additionalGuests, ticketQuantity };
  }

  /** Generates a message explaining why a user cannot proceed to checkout with a specific item, depending on different scenarios */
  private getItemInvalidMessage(item: IOrderItem, orderItems: IOrderItem[]): string | undefined {
    if (this.isInvalidMinPurchaseItem(item, orderItems)) {
      return this.translateService.instant('PRODUCT_MODAL.MIN_PURCHASE_MESSAGE', { AMOUNT: item.parentMinPurchase });
    }

    if (this.orderTicketRatiosService.isInvalidTicketRatio(item, orderItems)) {
      return this.orderTicketRatiosService.buildAlertMessage(item);
    }

    return undefined;
  }

  /** Determine if an item and the current basket items meet the minimum purchase requirements */
  private isInvalidMinPurchaseItem(item: IOrderItem, orderItems: IOrderItem[]): boolean {
    if (item.parentMinPurchase === 1) {
      return false;
    }

    const parentProductItems = orderItems.filter((orderItem) => {
      return (
        orderItem.parentProductId === item.parentProductId &&
        this.dateFormatService.isSameDay(orderItem.bookingDate, item.bookingDate) &&
        (!orderItem.session || this.productHelperService.isSameSession(orderItem.session, item.session))
      );
    });
    const totalQuantity = parentProductItems.reduce(
      (quantity, item) => quantity + item.quantity * item.groupSize + item.additionalGuests,
      0
    );
    return totalQuantity < item.parentMinPurchase;
  }

  private getOrderItemBookingDate(product: IProductInfo, bookingDate: Date): Date {
    const currentVenueTime = this.venueConfigService.getCurrentVenueTime();
    return product.type === 'membership' && product.recurringPayment ? currentVenueTime : bookingDate;
  }

  private getOrderItemGiftCard(parentProduct: IProduct, product: IProductInfo): IProductGiftcard | undefined {
    if (!parentProduct.giftcard || parentProduct.type !== 'giftcard') {
      return;
    }

    return {
      ...parentProduct.giftcard,
      productId: product.id
    };
  }

  private getPackageItemsForCalcs(packageOrderItems: IOrderItem[]): IOrderItem[] {
    const items: IOrderItem[] = [];
    packageOrderItems.forEach((packageOrderItem: IOrderItem) => {
      packageOrderItem.product.packageItems?.forEach((packageItem: IProductPackageItem) => {
        const packageItemCopy: IOrderItem = {
          ...structuredClone(packageOrderItem),
          quantity: packageOrderItem.quantity * packageItem.quantity,
          cost: packageItem.cost,
          tax: packageItem.tax ?? packageOrderItem.product.tax,
          isTaxInclusive: packageItem.isTaxInclusive ?? packageOrderItem.product.isTaxInclusive,
          perProductBookingFee: packageOrderItem.product.perProductBookingFee
        };

        items.push(packageItemCopy);
      });

      packageOrderItem.product.packageOptionSets?.forEach((optionSet) => {
        items.push({
          ...structuredClone(packageOrderItem),
          quantity: packageOrderItem.quantity * optionSet.quantity,
          cost: optionSet.cost,
          tax: packageOrderItem.product.tax,
          isTaxInclusive: packageOrderItem.product.isTaxInclusive,
          perProductBookingFee: packageOrderItem.product.perProductBookingFee
        });
      });
    });

    return items;
  }

  /**
   * When a package is added to the cart, we build an order item of each package item
   * This allows us to do flat calculations for capacity, without having to worry about the package structure
   */
  getPackageOrderItems(item: IOrderItem, bookingDate: Date): IOrderItem[] | undefined {
    if (item.productType !== 'package' || !item.packageItems?.length) {
      return undefined;
    }

    return item.packageItems.reduce((acc: IOrderItem[], packageItem) => {
      const packageItemParentProduct = this.productService.getParentProductAvailability(
        packageItem.parentProductId,
        bookingDate
      );
      const packageItemProduct = packageItemParentProduct?.products.find((p) => p.id === packageItem.productId);
      if (!packageItemParentProduct || !packageItemProduct) {
        this.loggerService.warn(
          `Failed to find package product for order item ${item.productId}, packageItemProductId: ${packageItem.productId}`
        );
        return acc;
      }

      const packageProductOrderItem = this.buildOrderItem(
        {
          id: packageItem.productId,
          quantity: packageItem.quantity,
          product: packageItemProduct,
          additionalGuests: item.additionalGuests,
          ticketQuantity: item.ticketQuantity
        },
        packageItemParentProduct,
        bookingDate,
        item.session,
        []
      );
      packageProductOrderItem.cost = packageItem.cost;

      return acc.concat(packageProductOrderItem);
    }, []);
  }

  private getPerProductFeeForCalcs(item: IOrderItem, isEditBooking: boolean): number {
    return isEditBooking ? 0 : (item.perProductBookingFee ?? 0);
  }
}
